<div class="flex h-full min-h-0 w-full flex-col overflow-hidden p-4">
  @if (loadingState() === 'loading') {
    <p class="m-0 text-sm text-secondary">Loading voucher…</p>
  } @else if (voucher(); as voucher) {
    <div class="flex h-full min-h-0 w-full flex-col gap-4 md:flex-row">
      <aside
        class="flex w-full shrink-0 flex-col rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950 md:w-1/3">
        <div class="flex flex-col gap-4 p-4">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Name</p>
              <h4 class="m-0 mt-1 break-words text-lg font-semibold text-primary">{{ voucher.name }}</h4>
            </div>
            <button
              type="button"
              class="inline-flex shrink-0 items-center justify-center rounded-md border border-zinc-200 p-1.5 text-secondary hover:bg-zinc-100 hover:text-primary dark:border-zinc-700 dark:hover:bg-zinc-800"
              [attr.aria-label]="'Copy ' + voucher.name"
              (click)="copyName(voucher.name)">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="size-4" aria-hidden="true">
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            </button>
          </div>

          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Created by</p>
            <p class="m-0 mt-1 text-sm font-medium text-primary">{{ voucher.createdByName || 'Unknown user' }}</p>
            <p class="m-0 mt-0.5 break-all text-xs text-secondary">
              @if (voucher.createdByFirebaseUid) {
                {{ voucher.createdByFirebaseUid }}
              } @else {
                —
              }
            </p>
          </div>

          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Created date</p>
            <p class="m-0 mt-1 text-sm text-primary">
              @if (voucher.createdDate) {
                {{ voucher.createdDate | date:'mediumDate' }}
              } @else {
                —
              }
            </p>
          </div>

          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">Expiry date</p>
            <p class="m-0 mt-1 text-sm text-primary">{{ voucher.expiresAt | date:'mediumDate' }}</p>
          </div>

          <div class="flex w-full min-w-0 flex-col gap-y-2">
            <div
              class="application-progress-bar"
              role="progressbar"
              [attr.aria-valuenow]="expiryBarWidth()"
              aria-valuemin="0"
              aria-valuemax="100"
              [attr.aria-label]="timeLeftLabel()">
              <div [class]="expiryFillClass()" [style.width.%]="expiryBarWidth()"></div>
            </div>
            <p class="m-0 text-xs text-secondary">{{ timeLeftLabel() }}</p>
          </div>
        </div>
      </aside>

      <section
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
        <div class="shrink-0 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
          <nav aria-label="Voucher usage views" class="flex space-x-2">
            <button
              type="button"
              class="rounded-md px-3 py-2 text-sm font-medium cursor-pointer"
              [class]="rightPanelTabClass('redeemed')"
              [attr.aria-current]="rightPanelTab() === 'redeemed' ? 'page' : null"
              (click)="selectRightPanelTab('redeemed')">
              Redeemed by
            </button>
            <button
              type="button"
              class="rounded-md px-3 py-2 text-sm font-medium cursor-pointer"
              [class]="rightPanelTabClass('usage')"
              [attr.aria-current]="rightPanelTab() === 'usage' ? 'page' : null"
              (click)="selectRightPanelTab('usage')">
              Usage
            </button>
          </nav>
          @if (rightPanelTab() === 'redeemed') {
            <p class="m-0 mt-2 text-xs text-secondary">
              {{ redemptions().length }} brand{{ redemptions().length === 1 ? '' : 's' }}
            </p>
          } @else {
            <p class="m-0 mt-2 text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">Per day</p>
          }
        </div>

        @if (rightPanelTab() === 'redeemed') {
          @if (redemptions().length === 0) {
            <p class="m-0 p-4 text-sm text-secondary">No brands have redeemed this voucher yet.</p>
          } @else {
            <div class="min-h-0 flex-1 overflow-auto scrollbar-transparent">
              <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
                <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-zinc-800/80">
                  <tr>
                    <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Brand</th>
                    <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">Date redeemed</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-950">
                  @for (row of redemptions(); track row.id) {
                    <tr>
                      <td class="px-4 py-3 text-sm text-primary">
                        <p class="m-0 font-medium">{{ row.companyName }}</p>
                      </td>
                      <td class="px-4 py-3 text-sm text-primary">
                        <p class="m-0">{{ row.redeemedAt | date:'medium' }}</p>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        } @else {
          <div class="flex min-h-0 flex-1 flex-col p-4">
            <app-echart-generic-chart
              class="block h-full min-h-60 w-full"
              [chartOption]="usageChart().chartOption"
              [chartXaxisData]="usageChart().chartXaxisData"
              [chartVariant]="usageChart().chartVariant" />
          </div>
        }
      </section>
    </div>
  } @else {
    <p class="m-0 text-sm text-secondary">This voucher could not be found.</p>
  }
</div>
